import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { isAuthenticated } from '@/lib/auth';
import { apiClient } from '@/lib/api-client';

type Tab = 'profile' | 'company' | 'security' | 'signing';

interface UserProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  status: string;
  emailVerified: boolean;
  createdAt: string;
}

interface PartnerInfo {
  id: string;
  name: string;
  allowedOrigins: string[];
  createdAt: string;
}

interface SecurityInfo {
  activeSessions: number;
  activeApiKeys: number;
  lastPasswordChange: string | null;
  twoFactorEnabled: boolean;
}

export default function Settings() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('profile');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Profile state
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: ''
  });

  // Company state
  const [partner, setPartner] = useState<PartnerInfo | null>(null);
  const [partnerForm, setPartnerForm] = useState({
    name: '',
    allowedOrigins: ''
  });

  // Security state
  const [security, setSecurity] = useState<SecurityInfo | null>(null);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Payment-link signing secret state
  const [signingSecret, setSigningSecret] = useState<string | null>(null);
  const [revealSecret, setRevealSecret] = useState(false);
  const [rotating, setRotating] = useState(false);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    loadData();
  }, [navigate, activeTab]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      if (activeTab === 'profile') {
        const response = await apiClient.get('/settings/profile');
        setProfile(response.data);
        setProfileForm({
          firstName: response.data.firstName,
          lastName: response.data.lastName
        });
      } else if (activeTab === 'company') {
        const response = await apiClient.get('/settings/partner');
        setPartner(response.data);
        setPartnerForm({
          name: response.data.name,
          allowedOrigins: response.data.allowedOrigins.join(', ')
        });
      } else if (activeTab === 'security') {
        const response = await apiClient.get('/settings/security');
        setSecurity(response.data);
      } else if (activeTab === 'signing') {
        const response = await apiClient.get('/settings/payment-signing-secret');
        setSigningSecret(response.data.paymentSigningSecret);
        setRevealSecret(false);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const handleRotateSigningSecret = async () => {
    if (
      !window.confirm(
        'Rotate your payment signing secret? Existing payment links signed with the old secret will stop verifying until you update your integration and re-sign them.'
      )
    ) {
      return;
    }
    setRotating(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await apiClient.post('/settings/payment-signing-secret/rotate');
      setSigningSecret(response.data.paymentSigningSecret);
      setRevealSecret(true);
      setSuccess(response.data.message || 'Payment signing secret rotated.');
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to rotate payment signing secret');
    } finally {
      setRotating(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await apiClient.put('/settings/profile', profileForm);
      setSuccess('Profile updated successfully');
      await loadData();
    } catch (error: any) {
      console.error('Failed to save profile:', error);
      setError(error.response?.data?.error || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePartner = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const origins = partnerForm.allowedOrigins
        .split(',')
        .map(o => o.trim())
        .filter(o => o);

      await apiClient.put('/settings/partner', {
        name: partnerForm.name,
        allowedOrigins: origins
      });

      setSuccess('Company settings updated successfully');
      await loadData();
    } catch (error: any) {
      console.error('Failed to save partner settings:', error);
      setError(error.response?.data?.error || 'Failed to save company settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await apiClient.post('/settings/password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });

      setSuccess('Password changed successfully. Other sessions have been logged out.');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error: any) {
      console.error('Failed to change password:', error);
      setError(error.response?.data?.error || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const handleRevokeAllSessions = async () => {
    if (!confirm('This will log out all other devices. Continue?')) {
      return;
    }

    try {
      await apiClient.post('/settings/sessions/revoke-all');
      setSuccess('All other sessions have been logged out');
      await loadData();
    } catch (error: any) {
      console.error('Failed to revoke sessions:', error);
      setError(error.response?.data?.error || 'Failed to revoke sessions');
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-96">
          <div className="text-gray-600">Loading settings...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600 mt-1">Manage your account and company settings</p>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Success Alert */}
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
            {success}
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('profile')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'profile'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Profile
            </button>
            <button
              onClick={() => setActiveTab('company')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'company'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Company
            </button>
            <button
              onClick={() => setActiveTab('security')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'security'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Security
            </button>
            <button
              onClick={() => setActiveTab('signing')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'signing'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Payment Links
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'profile' && profile && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
                <CardDescription>Update your personal details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name
                    </label>
                    <Input
                      type="text"
                      value={profileForm.firstName}
                      onChange={(e) =>
                        setProfileForm({ ...profileForm, firstName: e.target.value })
                      }
                      disabled={saving}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name
                    </label>
                    <Input
                      type="text"
                      value={profileForm.lastName}
                      onChange={(e) =>
                        setProfileForm({ ...profileForm, lastName: e.target.value })
                      }
                      disabled={saving}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <Input type="email" value={profile.email} readOnly disabled />
                  <p className="text-sm text-gray-500 mt-1">
                    Email cannot be changed. Contact support if needed.
                  </p>
                </div>

                <Button onClick={handleSaveProfile} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Account Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Role:</span>{' '}
                    <span className="font-medium">{profile.role}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Status:</span>{' '}
                    <span className="font-medium">{profile.status}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Email Verified:</span>{' '}
                    <span className="font-medium">
                      {profile.emailVerified ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Member Since:</span>{' '}
                    <span className="font-medium">{formatDate(profile.createdAt)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'company' && partner && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Company Information</CardTitle>
                <CardDescription>Update your company details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name
                  </label>
                  <Input
                    type="text"
                    value={partnerForm.name}
                    onChange={(e) =>
                      setPartnerForm({ ...partnerForm, name: e.target.value })
                    }
                    disabled={saving}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Allowed Origins (comma-separated URLs)
                  </label>
                  <Input
                    type="text"
                    value={partnerForm.allowedOrigins}
                    onChange={(e) =>
                      setPartnerForm({ ...partnerForm, allowedOrigins: e.target.value })
                    }
                    placeholder="https://example.com, https://app.example.com"
                    disabled={saving}
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Allowed origins for CORS. Separate multiple URLs with commas.
                  </p>
                </div>

                <Button onClick={handleSavePartner} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Partner ID</CardTitle>
                <CardDescription>Your unique partner identifier</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="font-mono text-sm bg-gray-50 px-4 py-2 rounded border border-gray-200">
                  {partner.id}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'security' && security && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Change Password</CardTitle>
                <CardDescription>Update your account password</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Current Password
                  </label>
                  <Input
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(e) =>
                      setPasswordForm({ ...passwordForm, currentPassword: e.target.value })
                    }
                    disabled={saving}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Password
                  </label>
                  <Input
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) =>
                      setPasswordForm({ ...passwordForm, newPassword: e.target.value })
                    }
                    disabled={saving}
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Minimum 8 characters with uppercase, lowercase, and numbers
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm New Password
                  </label>
                  <Input
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) =>
                      setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })
                    }
                    disabled={saving}
                  />
                </div>

                <Button onClick={handleChangePassword} disabled={saving}>
                  {saving ? 'Changing Password...' : 'Change Password'}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Security Overview</CardTitle>
                <CardDescription>Account security information</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Active Sessions</div>
                    <div className="text-2xl font-bold">{security.activeSessions}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Active API Keys</div>
                    <div className="text-2xl font-bold">{security.activeApiKeys}</div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-sm text-gray-600 mb-1">Last Password Change</div>
                    <div className="text-lg font-medium">
                      {formatDate(security.lastPasswordChange)}
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <Button onClick={handleRevokeAllSessions} variant="outline">
                    Log Out All Other Sessions
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'signing' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Payment Signing Secret</CardTitle>
                <CardDescription>
                  Pass this to the SDK as <code className="font-mono">PaymentUrlBuilder({'{ jwtSecret }'})</code>{' '}
                  to sign payment links. It is different from your webhook secret and your API key —
                  keep it on your server only, never in browser code.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {signingSecret ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Secret
                      </label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="text"
                          readOnly
                          className="font-mono text-sm"
                          value={revealSecret ? signingSecret : '•'.repeat(Math.min(signingSecret.length, 48))}
                        />
                        <Button variant="outline" onClick={() => setRevealSecret((v) => !v)}>
                          {revealSecret ? 'Hide' : 'Reveal'}
                        </Button>
                        <Button variant="outline" onClick={() => copyToClipboard(signingSecret)}>
                          Copy
                        </Button>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-gray-200">
                      <p className="text-sm text-gray-500 mb-2">
                        Rotate the secret if it has leaked. Existing payment links signed with the
                        old secret will stop verifying until you update your integration.
                      </p>
                      <Button variant="outline" onClick={handleRotateSigningSecret} disabled={rotating}>
                        {rotating ? 'Rotating...' : 'Rotate Secret'}
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-gray-500">Loading secret…</div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
